import type { ChangeEvent } from "react";
import { useCallback, useMemo, useState } from "react";
import { Box, Input as MuiInput, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { ReactComponent as SearchIcon } from "../../assets/svg/Search.svg";
import { LabelBox, Button } from "../../components";
import { colors } from "../../theme/colors";
import {
  useGetSitesQuery,
  useDeleteSiteMutation,
  useGetOrgansQuery,
} from "../../services/api";
import { CustomTable } from "../../components/DataTable";
import style from "./site.module.css";
import { CustomActionMenu } from "../../components/Menu";
import type { ActionItem } from "../../components/Menu";
import { ReactComponent as EditIcon } from "../../assets/svg/edit.svg";
import { ConfirmationDialog } from "../../components/Dialog";
import { getErrorMessage } from "../../utils/common";

type SiteAction = "edit" | "delete";

/**
 * Sites listing page with search, edit and delete row actions.
 *
 * Notes:
 * - Sites are hard-deleted rather than deactivated, so there is no status
 *   column, no reason prompt and no logs dialog.
 */
const Sites = () => {
  const [deleteSite] = useDeleteSiteMutation();
  const navigate = useNavigate();

  // all query parameters and pagination params
  const [queryParam] = useState({
    order_by_columns: "name",
    order: "Asc",
    is_detailed: true,
  });
  const { data: siteData, isFetching } = useGetSitesQuery(queryParam);

  // With is_detailed=true the site list normally carries organ_name already.
  // Only fetch the organ list as a fallback when at least one row is missing it,
  // so the common case does not fire a redundant request on every mount.
  const needsOrganLookup = useMemo(
    () => (siteData?.items ?? []).some((site: Site) => site.organ_name == null),
    [siteData]
  );

  // Fallback: resolve organ_id -> name for any rows that arrived without an
  // organ_name rather than showing a bare id.
  const { data: organData } = useGetOrgansQuery(
    {
      order_by_columns: "name",
      order: "Asc",
      is_detailed: false,
    },
    { skip: !needsOrganLookup }
  );

  // The site endpoint takes no search parameter, so filtering happens here
  // over the fetched rows rather than as part of the query.
  const [searchTerm, setSearchTerm] = useState("");

  const organNameById = useMemo(() => {
    const lookup = new Map<number, string>();
    (organData?.items ?? []).forEach((organ: Organ) => {
      lookup.set(organ.id, organ.name);
    });
    return lookup;
  }, [organData]);

  const sites: Site[] = useMemo(() => {
    const items: Site[] = siteData?.items ?? [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return items;
    // Match the two columns the user can actually see.
    return items.filter((site) => {
      const organName =
        site.organ_name ??
        (site.organ_id != null ? organNameById.get(site.organ_id) : undefined);
      return (
        site.name?.toLowerCase().includes(term) ||
        organName?.toLowerCase().includes(term)
      );
    });
  }, [siteData, searchTerm, organNameById]);

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handles action menu selections for a site row.
   *
   * Args:
   *   value (SiteAction): Action selected from the row menu.
   *   item (Site): Site associated with the action.
   *
   * Returns:
   *   void
   */
  const handleMenu = useCallback(
    (value: SiteAction, item: Site) => {
      setSelectedSite(item);
      switch (value) {
        case "edit":
          // Hand the row over so the edit screen does not have to re-fetch it.
          navigate(`/sites/edit/${item?.id}`, { state: { site: item } });
          break;
        case "delete":
          setModalVisible(true);
          break;
      }
    },
    [navigate]
  );

  /**
   * Column definitions for the Sites table.
   *
   * Returns:
   *   Array<object>: Column configuration for CustomTable.
   */
  const fields = useMemo(
    () => [
      { id: "name", label: "Site Name", flex: 2, sortable: true },
      {
        id: "organ_name",
        label: "Organ",
        flex: 2,
        sortable: true,
        // Prefer a name the API sent; otherwise resolve organ_id against the
        // lookup. A dash covers a site whose organ has since been deleted.
        render: (item: Site) => (
          <Typography className={style.cell}>
            {item.organ_name ??
              (item.organ_id != null ? organNameById.get(item.organ_id) : undefined) ??
              "-"}
          </Typography>
        ),
      },
      {
        id: "actions",
        label: "Actions",
        type: "action",
        render: (item: Site) => (
          <CustomActionMenu<Site, SiteAction>
            item={item}
            isActive={item.is_active ?? false}
            handleChange={handleMenu}
            actions={
              [
                {
                  label: "Edit",
                  value: "edit",
                  icon: <EditIcon height={24} width={24} />,
                },
                {
                  label: "Delete",
                  value: "delete",
                  icon: <DeleteOutlineIcon height={24} width={24} />,
                },
              ] as ActionItem<SiteAction>[]
            }
          />
        ),
      },
    ],
    [handleMenu, organNameById]
  );

  /**
   * Updates the search term in query params.
   *
   * Args:
   *   e (ChangeEvent<HTMLInputElement>): Search input change event.
   *
   * Returns:
   *   void
   */
  const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  /**
   * Deletes the selected site.
   *
   * Returns:
   *   Promise<void>
   *
   * Notes:
   * - Deletion is permanent; the confirmation dialog is the only safeguard.
   */
  const handleDelete = async () => {
    try {
      // Guard against submitting without a selected row; avoids invalid deletes.
      if (!selectedSite) {
        setMessage({
          type: "error",
          text: "Please select a site before deleting.",
        });
        return;
      }
      setIsLoading(true);
      await deleteSite(selectedSite.id).unwrap();
      setIsLoading(false);
      setModalVisible(false);
      setSelectedSite(null);
    } catch (error: unknown) {
      setIsLoading(false);
      setModalVisible(false);
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Something went wrong!"),
      });
    }
  };

  // Render the Site component
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "15px",
        }}
      >
        <MuiInput
          disableUnderline
          placeholder="Search by name...."
          type="search"
          startAdornment={<SearchIcon width={20} height={20} />}
          value={searchTerm}
          onChange={handleSearch}
          sx={{ width: "550px", gap: "8px", borderRadius: "50px", backgroundColor: colors.white }}
        />
        <Link to="add">
          <Button
            variant="contained"
            className={style.addButton}
            label="Add New Site"
            sx={{
              ":hover": { backgroundColor: colors.primary },
            }}
          />
        </Link>
      </Box>
      {message && (
        <Box mt={2}>
          <LabelBox
            boxProps={{ height: 50, display: "flex", alignItems: "center" }}
            data={message}
            onClose={() => setMessage(null)}
          />
        </Box>
      )}
      <Box style={{ maxHeight: "calc(100vh - 195px)", overflow: "auto" }}>
        <CustomTable<Site>
          fields={fields}
          paginationRequired={true}
          data={sites}
          loading={isFetching}
          onDoubleClick={(site: Site) => {
            navigate(`/sites/edit/${site?.id}`, { state: { site } });
          }}
          defaultOrderBy="name"
          searchTerm={searchTerm}
          onClearSearch={() => setSearchTerm("")}
        />
      </Box>
      {modalVisible && (
        <ConfirmationDialog
          visible={modalVisible}
          message={`Are you sure you want to delete "${selectedSite?.name}"? This cannot be undone.`}
          loading={isLoading}
          onClose={() => {
            setModalVisible(false);
          }}
          onConfirm={handleDelete}
        />
      )}
    </Box>
  );
}
export default Sites;
